import { useCallback, useRef, useState } from 'react';
import {
  fetchPumpAccountingCases,
  fetchPumpAccountingFixes,
  fetchPumpAccountingStats,
  fetchPumpAccountingWacAudits,
  friendlyFetchError,
} from '../adminApi';

/**
 * Lazy-loads accounting metadata per tab — nothing loads on mount except empty state.
 */
export function useAccountingModule(pumpId) {
  const [fixes, setFixes] = useState([]);
  const [fixesIntro, setFixesIntro] = useState(null);
  const [repairScripts, setRepairScripts] = useState([]);
  const [repairScriptsIntro, setRepairScriptsIntro] = useState(null);
  const [wacAudits, setWacAudits] = useState([]);
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [casesPagination, setCasesPagination] = useState({});
  const [caseFilter, setCaseFilter] = useState('');

  const [catalogReady, setCatalogReady] = useState(false);
  const [toolsReady, setToolsReady] = useState(false);
  const [historyReady, setHistoryReady] = useState(false);

  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingTools, setLoadingTools] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [errors, setErrors] = useState({});
  const [activityLog, setActivityLog] = useState([]);

  const catalogPromise = useRef(null);
  const toolsPromise = useRef(null);

  const pushActivity = useCallback((message, tone = 'info') => {
    const entry = { id: Date.now(), message, tone, at: new Date().toLocaleTimeString() };
    setActivityLog((prev) => [entry, ...prev].slice(0, 8));
  }, []);

  const loadCatalog = useCallback(async () => {
    if (catalogReady) return true;
    if (catalogPromise.current) return catalogPromise.current;

    setLoadingCatalog(true);
    pushActivity('Loading fix catalog…');

    catalogPromise.current = fetchPumpAccountingFixes(pumpId)
      .then((res) => {
        setFixes(res.fixes || []);
        setFixesIntro(res.intro || null);
        setRepairScripts(res.repair_scripts || []);
        setRepairScriptsIntro(res.repair_scripts_intro || null);
        setCatalogReady(true);
        pushActivity('Fix catalog ready', 'success');
        return true;
      })
      .catch((err) => {
        const msg = friendlyFetchError(err);
        setErrors((e) => ({ ...e, catalog: msg }));
        pushActivity(`Fix catalog failed: ${msg}`, 'error');
        return false;
      })
      .finally(() => {
        setLoadingCatalog(false);
        catalogPromise.current = null;
      });

    return catalogPromise.current;
  }, [pumpId, catalogReady, pushActivity]);

  const loadToolsData = useCallback(async () => {
    const catalogOk = await loadCatalog();
    if (!catalogOk || toolsReady) return catalogOk;

    if (toolsPromise.current) return toolsPromise.current;

    setLoadingTools(true);
    pushActivity('Loading repair tools & WAC history…');

    toolsPromise.current = fetchPumpAccountingWacAudits(pumpId)
      .then((res) => {
        setWacAudits(res.data || []);
        setToolsReady(true);
        pushActivity('Repair tools ready', 'success');
        return true;
      })
      .catch((err) => {
        const msg = friendlyFetchError(err);
        setErrors((e) => ({ ...e, tools: msg }));
        pushActivity(`Repair tools failed: ${msg}`, 'error');
        return false;
      })
      .finally(() => {
        setLoadingTools(false);
        toolsPromise.current = null;
      });

    return toolsPromise.current;
  }, [pumpId, loadCatalog, toolsReady, pushActivity]);

  const loadHistoryData = useCallback(async (page = 1, fixType = caseFilter) => {
    setLoadingHistory(true);
    pushActivity(page === 1 ? 'Loading audit trail…' : `Loading audit page ${page}…`);

    try {
      const [statsRes, casesRes] = await Promise.all([
        fetchPumpAccountingStats(pumpId),
        fetchPumpAccountingCases(pumpId, page, fixType),
      ]);

      if (statsRes?.status) {
        setStats(statsRes);
      }
      setCases(casesRes.data || []);
      setCasesPagination(casesRes.pagination || {});
      setHistoryReady(true);
      pushActivity(`Audit trail loaded (${(casesRes.data || []).length} cases on this page)`, 'success');
      return true;
    } catch (err) {
      const msg = friendlyFetchError(err);
      setErrors((e) => ({ ...e, history: msg }));
      pushActivity(`Audit trail failed: ${msg}`, 'error');
      return false;
    } finally {
      setLoadingHistory(false);
    }
  }, [pumpId, caseFilter, pushActivity]);

  const loadCases = useCallback((page, fixType = caseFilter) => {
    return loadHistoryData(page, fixType);
  }, [loadHistoryData, caseFilter]);

  const refreshAfterFix = useCallback(() => {
    setHistoryReady(false);
    loadCatalog();
    if (historyReady) {
      loadHistoryData(casesPagination.current_page || 1, caseFilter);
    }
    if (toolsReady) {
      fetchPumpAccountingWacAudits(pumpId)
        .then((res) => setWacAudits(res.data || []))
        .catch(() => {});
    }
  }, [loadCatalog, loadHistoryData, historyReady, toolsReady, pumpId, casesPagination.current_page, caseFilter]);

  const onSectionEnter = useCallback((sectionId) => {
    if (sectionId === 'tools') {
      loadToolsData();
    } else if (sectionId === 'history') {
      loadCatalog();
      loadHistoryData(1, caseFilter);
    } else if (sectionId === 'playbook') {
      loadCatalog();
    }
  }, [loadToolsData, loadHistoryData, loadCatalog, caseFilter]);

  return {
    fixes,
    fixesIntro,
    repairScripts,
    repairScriptsIntro,
    wacAudits,
    stats,
    cases,
    casesPagination,
    caseFilter,
    setCaseFilter,
    catalogReady,
    toolsReady,
    historyReady,
    loadingCatalog,
    loadingTools,
    loadingHistory,
    errors,
    activityLog,
    pushActivity,
    loadCatalog,
    loadToolsData,
    loadHistoryData,
    loadCases,
    refreshAfterFix,
    onSectionEnter,
  };
}
